import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { studentPortalAPI } from '../services/api';

const StudentAuthContext = createContext(null);

const STORAGE_KEY_TOKEN   = 'student_portal_token';
const STORAGE_KEY_STUDENT = 'student_portal_user';
const STORAGE_KEY_EXPIRES = 'student_portal_expires';

export const StudentAuthProvider = ({ children }) => {
  const [student, setStudent]   = useState(null);
  const [token, setToken]       = useState(null);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    const storedToken   = localStorage.getItem(STORAGE_KEY_TOKEN);
    const storedStudent = localStorage.getItem(STORAGE_KEY_STUDENT);
    const expiresAt     = localStorage.getItem(STORAGE_KEY_EXPIRES);

    if (storedToken && storedStudent) {
      if (expiresAt && new Date(expiresAt) < new Date()) {
        clearAuth();
        setLoading(false);
        return;
      }
      studentPortalAPI.getProfile(storedToken)
        .then((res) => {
          setToken(storedToken);
          setStudent(res.data);
          localStorage.setItem(STORAGE_KEY_STUDENT, JSON.stringify(res.data));
        })
        .catch(() => clearAuth())
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = useCallback((studentData, authToken, expiresAt) => {
    setStudent(studentData);
    setToken(authToken);
    localStorage.setItem(STORAGE_KEY_TOKEN,   authToken);
    localStorage.setItem(STORAGE_KEY_STUDENT, JSON.stringify(studentData));
    localStorage.setItem(STORAGE_KEY_EXPIRES, expiresAt);
  }, []);

  const logout = useCallback(async () => {
    const t = token || localStorage.getItem(STORAGE_KEY_TOKEN);
    if (t) {
      try { await studentPortalAPI.logout(t); } catch { /* silent */ }
    }
    clearAuth();
  }, [token]);

  const clearAuth = () => {
    setStudent(null);
    setToken(null);
    localStorage.removeItem(STORAGE_KEY_TOKEN);
    localStorage.removeItem(STORAGE_KEY_STUDENT);
    localStorage.removeItem(STORAGE_KEY_EXPIRES);
  };

  const updateStudent = useCallback((data) => {
    setStudent(data);
    localStorage.setItem(STORAGE_KEY_STUDENT, JSON.stringify(data));
  }, []);

  return (
    <StudentAuthContext.Provider value={{ student, token, loading, login, logout, updateStudent }}>
      {children}
    </StudentAuthContext.Provider>
  );
};

export const useStudentAuth = () => {
  const ctx = useContext(StudentAuthContext);
  if (!ctx) throw new Error('useStudentAuth must be used within StudentAuthProvider');
  return ctx;
};
